import { z } from 'zod';

/**
 * Zod schemas for ATC manifest validation
 * 
 * These schemas validate data at the boundary (adapter layer) before
 * it enters the application. They provide runtime type safety for
 * external API data that TypeScript cannot verify at compile time.
 * 
 * @see https://cdn.atc.chillairwaves.com/manifest.json
 */

// ============================================================================
// Station Info Schema (entries in main manifest)
// ============================================================================

/**
 * Schema for optional station-level radio effect presets.
 *
 * Mirrors the JSON payloads generated by the radio-audio-effect tooling while
 * allowing forward-compatible extra keys that the web app can safely ignore.
 */
export const AtcRadioEffectPresetSchema = z.object({
  seed: z.number().nullable().optional(),
  target_sr: z.number().nullable().optional(),
  mono: z.boolean().optional(),
  mix: z.number().optional(),
  preserve_loudness: z.boolean().optional(),
  bp_lo: z.number().optional(),
  bp_hi: z.number().optional(),
  bp_order: z.number().optional(),
  preemph: z.number().optional(),
  comp_thresh_db: z.number().optional(),
  comp_ratio: z.number().optional(),
  comp_window_ms: z.number().optional(),
  comp_attack_ms: z.number().optional(),
  comp_release_ms: z.number().optional(),
  makeup_db: z.number().optional(),
  drive: z.number().optional(),
  clip_lpf_hz: z.number().optional(),
  noise_snr_db: z.number().optional(),
  noise_lo: z.number().optional(),
  noise_hi: z.number().optional(),
  hiss_mod_depth: z.number().optional(),
  hiss_mod_rate_hz: z.number().optional(),
  hiss_mode: z.enum(['always', 'gated']).optional(),
  fade_depth: z.number().optional(),
  fade_rate_hz: z.number().optional(),
  dropouts: z.number().optional(),
  dropout_min_ms: z.number().optional(),
  dropout_max_ms: z.number().optional(),
  dropout_floor: z.number().optional(),
  gate_rms_window: z.number().optional(),
  gate_percentile: z.number().optional(),
  gate_mult: z.number().optional(),
  gate_hang_ms: z.number().optional(),
  squelch_tail_ms: z.number().optional(),
  peak: z.number().optional(),
  archive_mode: z.string().min(1).optional(),
  archive_sr: z.number().nullable().optional(),
  archive_lowpass_hz: z.number().optional(),
  archive_codec: z.string().min(1).optional(),
  mp3_bitrate: z.string().nullable().optional(),
}).passthrough();

export type ApiAtcRadioEffectPreset = z.infer<typeof AtcRadioEffectPresetSchema>;

/**
 * Schema for a station entry in the main manifest
 * Validates API response format with snake_case fields
 */
export const AtcStationInfoSchema = z.object({
  /** ICAO airport code (e.g., "KJFK", "KSFO") */
  airport_code: z
    .string()
    .min(3, 'Airport code must be at least 3 characters')
    .max(4, 'Airport code must be at most 4 characters')
    .regex(/^[A-Z0-9]+$/i, 'Airport code must be alphanumeric'),
  /** Type of ATC station (e.g., "approach", "tower", "ground") */
  station_type: z
    .string()
    .min(1, 'Station type cannot be empty')
    .max(50, 'Station type is too long'),
  /** Number of available scenarios */
  scenario_count: z
    .number()
    .int('Scenario count must be an integer')
    .nonnegative('Scenario count cannot be negative'),
  /** Total size of all scenarios in bytes */
  total_size_bytes: z
    .number()
    .int('Total size must be an integer')
    .nonnegative('Total size cannot be negative'),
  /** URL to the station's manifest.json */
  manifest_url: z
    .string()
    .url('Manifest URL must be a valid URL'),

  /** Optional airport name */
  airport_name: z.string().min(1).nullable().optional(),
  /** Optional IATA code */
  iata_code: z.string().min(1).nullable().optional(),
  /** Optional ICAO code override */
  icao_code: z.string().min(1).nullable().optional(),
  /** Optional local code */
  local_code: z.string().min(1).nullable().optional(),
  /** Optional city */
  city: z.string().min(1).nullable().optional(),
  /** Optional country */
  country: z.string().min(1).nullable().optional(),
  /** Optional time zone */
  time_zone: z.string().min(1).nullable().optional(),
  /** Optional station-specific radio effect preset payload */
  radio_effect_preset: AtcRadioEffectPresetSchema.nullable().optional(),
}).strict();

export type ApiAtcStationInfo = z.infer<typeof AtcStationInfoSchema>;

/**
 * Schema for airport entry in v2 root manifest
 */
export const AtcAirportInfoSchema = z.object({
  airport_code: z
    .string()
    .min(3, 'Airport code must be at least 3 characters')
    .max(4, 'Airport code must be at most 4 characters')
    .regex(/^[A-Z0-9]+$/i, 'Airport code must be alphanumeric'),
  name: z.string().min(1, 'Airport name is required'),
  icao_code: z
    .string()
    .min(3, 'ICAO code must be at least 3 characters')
    .max(4, 'ICAO code must be at most 4 characters')
    .regex(/^[A-Z0-9]+$/i, 'ICAO code must be alphanumeric'),
  iata_code: z.string().min(1).nullable(),
  local_code: z.string().min(1).nullable(),
  city: z.string().min(1).nullable(),
  country: z.string().min(1).nullable(),
  time_zone: z.string().min(1).nullable(),
  stations: z.array(AtcStationInfoSchema).min(0, 'Stations array is required'),
  station_count: z
    .number()
    .int('Station count must be an integer')
    .nonnegative('Station count cannot be negative'),
  total_scenario_count: z
    .number()
    .int('Total scenario count must be an integer')
    .nonnegative('Total scenario count cannot be negative'),
  total_size_bytes: z
    .number()
    .int('Total size must be an integer')
    .nonnegative('Total size cannot be negative'),
}).strict();

export type ApiAtcAirportInfo = z.infer<typeof AtcAirportInfoSchema>;

// ============================================================================
// Main Manifest Schema
// ============================================================================

/**
 * Schema for the main manifest.json from the CDN
 * URL: https://cdn.atc.chillairwaves.com/manifest.json
 */
export const AtcMainManifestV1Schema = z.object({
  /** List of available stations */
  stations: z
    .array(AtcStationInfoSchema)
    .min(0, 'Stations array is required'),
  /** Total size of all audio files in bytes */
  total_size_bytes: z
    .number()
    .int('Total size must be an integer')
    .nonnegative('Total size cannot be negative'),
  /** Total number of scenarios across all stations */
  total_scenario_count: z
    .number()
    .int('Total scenario count must be an integer')
    .nonnegative('Total scenario count cannot be negative'),
  /** Number of stations */
  station_count: z
    .number()
    .int('Station count must be an integer')
    .nonnegative('Station count cannot be negative'),
  /** Last update timestamp (ISO 8601) */
  updated_at: z.string().min(1, 'Updated at timestamp is required'),
  /** Manifest version */
  version: z.string().min(1, 'Version is required'),
}).strict();

export const AtcMainManifestV2Schema = z.object({
  /** Grouped airport entries */
  airports: z
    .array(AtcAirportInfoSchema)
    .min(0, 'Airports array is required'),
  /** Optional flat station list for compatibility */
  stations: z
    .array(AtcStationInfoSchema)
    .optional(),
  /** Number of airports */
  airport_count: z
    .number()
    .int('Airport count must be an integer')
    .nonnegative('Airport count cannot be negative'),
  /** Total size of all audio files in bytes */
  total_size_bytes: z
    .number()
    .int('Total size must be an integer')
    .nonnegative('Total size cannot be negative'),
  /** Total number of scenarios across all stations */
  total_scenario_count: z
    .number()
    .int('Total scenario count must be an integer')
    .nonnegative('Total scenario count cannot be negative'),
  /** Number of stations */
  station_count: z
    .number()
    .int('Station count must be an integer')
    .nonnegative('Station count cannot be negative'),
  /** Last update timestamp (ISO 8601) */
  updated_at: z.string().min(1, 'Updated at timestamp is required'),
  /** Manifest version */
  version: z.string().min(1, 'Version is required'),
}).strict();

export const AtcMainManifestSchema = z.union([
  AtcMainManifestV2Schema,
  AtcMainManifestV1Schema,
]);

export type ApiAtcMainManifest = z.infer<typeof AtcMainManifestSchema>;

// ============================================================================
// Scenario Schema
// ============================================================================

/**
 * Regex pattern for UUID validation
 * Accepts both standard format (with hyphens) and compact format (without hyphens)
 * - Standard: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (36 chars)
 * - Compact:  xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx (32 chars, hex only)
 */
const UUID_PATTERN = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;

/**
 * Schema for a scenario entry in a station manifest
 */
export const AtcScenarioSchema = z.object({
  /** Unique scenario identifier (UUID - standard or compact format) */
  scenario_id: z
    .string()
    .regex(UUID_PATTERN, 'Scenario ID must be a valid UUID (standard or compact format)'),
  /** File size in bytes */
  size_bytes: z
    .number()
    .int('Size must be an integer')
    .nonnegative('Size cannot be negative'),
  /** Upload timestamp (ISO 8601) */
  uploaded_at: z.string().min(1, 'Uploaded at timestamp is required'),
  /** Direct CDN URL to the MP3 file */
  cdn_url: z
    .string()
    .url('CDN URL must be a valid URL'),
}).strict();

export type ApiAtcScenario = z.infer<typeof AtcScenarioSchema>;

// ============================================================================
// Station Manifest Schema
// ============================================================================

/**
 * Schema for a station manifest.json
 * URL: https://cdn.atc.chillairwaves.com/{airport_code}/{station_type}/manifest.json
 */
export const AtcStationManifestSchema = z.object({
  /** ICAO airport code */
  airport_code: z
    .string()
    .min(3, 'Airport code must be at least 3 characters')
    .max(4, 'Airport code must be at most 4 characters')
    .regex(/^[A-Z0-9]+$/i, 'Airport code must be alphanumeric'),
  /** Type of ATC station */
  station_type: z
    .string()
    .min(1, 'Station type cannot be empty')
    .max(50, 'Station type is too long'),
  /** List of available scenarios */
  scenarios: z
    .array(AtcScenarioSchema)
    .min(0, 'Scenarios array is required'),
  /** Total size of all scenarios in bytes */
  total_size_bytes: z
    .number()
    .int('Total size must be an integer')
    .nonnegative('Total size cannot be negative'),
  /** Number of scenarios */
  scenario_count: z
    .number()
    .int('Scenario count must be an integer')
    .nonnegative('Scenario count cannot be negative'),
  /** Last update timestamp (ISO 8601) */
  updated_at: z.string().min(1, 'Updated at timestamp is required'),
}).strict();

export type ApiAtcStationManifest = z.infer<typeof AtcStationManifestSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Parse and validate main manifest data
 * @throws {z.ZodError} if validation fails
 */
export function parseMainManifest(data: unknown): ApiAtcMainManifest {
  return AtcMainManifestSchema.parse(data);
}

/**
 * Safe parse main manifest (returns result object instead of throwing)
 */
export function safeParseMainManifest(data: unknown) {
  return AtcMainManifestSchema.safeParse(data);
}

/**
 * Parse and validate station manifest data
 * @throws {z.ZodError} if validation fails
 */
export function parseStationManifest(data: unknown): ApiAtcStationManifest {
  return AtcStationManifestSchema.parse(data);
}

/**
 * Safe parse station manifest (returns result object instead of throwing)
 */
export function safeParseStationManifest(data: unknown) {
  return AtcStationManifestSchema.safeParse(data);
}

/**
 * Parse and validate station info data
 * @throws {z.ZodError} if validation fails
 */
export function parseStationInfo(data: unknown): ApiAtcStationInfo {
  return AtcStationInfoSchema.parse(data);
}

/**
 * Parse and validate scenario data
 * @throws {z.ZodError} if validation fails
 */
export function parseScenario(data: unknown): ApiAtcScenario {
  return AtcScenarioSchema.parse(data);
}
